/**
 * "Manage" an advertiser page: label + toggles, the campaign roster (include /
 * exclude every discovered order, open or create its child page), advertiser
 * aliases and specific-order includes, and the last sync's summary. Saves a
 * PATCH with only the fields that changed; a cross-advertiser include (422)
 * must be confirmed by typing the foreign advertiser's name.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Button, Modal, Spinner, StatusPill } from '../../ui';
import type { ReportableOrder } from '../../../services/campaignReportsApi';
import {
  AdvertiserCandidateGroup,
  AdvertiserPageDetail,
  CrossAdvertiserOrder,
  HubRosterRow,
  UpdateAdvertiserPageInput,
  getAdvertiserPage,
  isCrossAdvertiserError,
  updateAdvertiserPage,
} from '../../../services/advertiserReportsApi';
import {
  Notify,
  advertiserPageUrl,
  campaignPageUrl,
  describeSync,
  fieldStyle,
  fmtDate,
  fmtDateTimePt,
  helpStyle,
  labelStyle,
  matchesQuery,
  plural,
  relatedOrdersForHub,
  sameIdList,
  sameIdSet,
  syncIssueLines,
  syncReasonLabel,
} from './shared';
import { LifecyclePill } from './pills';
import CrossAdvertiserConfirm, { crossAdvertiserConfirmed } from './CrossAdvertiserConfirm';

interface ManageAdvertiserPageModalProps {
  slug: string;
  /** Picker groups — alias search and specific-order includes. */
  groups: AdvertiserCandidateGroup[];
  /** Bump to re-fetch the page (e.g. after a child page was created from here). */
  refreshKey?: number;
  onClose: () => void;
  /** After a successful save — the caller reloads the lists. */
  onSaved: () => void;
  /** Open the campaign create modal pre-linked to this hub. */
  onCreateCampaign: (order: ReportableOrder, hub: { slug: string; label: string }) => void;
  notify: Notify;
}

interface PendingInclude {
  gamOrderName: string;
  advertiserName: string;
  advertiserId: string;
}

interface CrossConfirmState {
  orders: CrossAdvertiserOrder[];
  patch: UpdateAdvertiserPageInput;
  typed: string;
}

const MAX_SEARCH_RESULTS = 8;

const chipStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '3px 9px',
  borderRadius: 'var(--radius-pill)',
  fontSize: 12,
  background: 'var(--cg-neutral-100)',
  color: 'var(--fg-2)',
};

const chipRemoveStyle: React.CSSProperties = {
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  padding: 0,
  color: 'var(--fg-4)',
  fontSize: 12,
  lineHeight: 1,
};

const resultListStyle: React.CSSProperties = {
  border: '1px solid var(--border-1)',
  borderRadius: 'var(--radius-md)',
  marginTop: 4,
  overflow: 'hidden',
};

const resultRowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 10,
  width: '100%',
  padding: '7px 10px',
  border: 'none',
  borderTop: '1px solid var(--border-subtle)',
  background: 'var(--bg-surface-1)',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 12.5,
  textAlign: 'left',
  color: 'var(--fg-1)',
};

const rosterRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'auto minmax(0, 1fr) auto minmax(180px, 220px)',
  gap: 10,
  alignItems: 'center',
  padding: '8px 10px',
  borderTop: '1px solid var(--border-subtle)',
  fontSize: 12.5,
};

/** Build a picker-shaped order from a roster row so the campaign create modal can take it. */
const toReportableOrder = (row: HubRosterRow, hubSlug: string): ReportableOrder => ({
  gamOrderId: row.gamOrderId,
  gamOrderName: row.gamOrderName,
  advertiserId: row.advertiserId,
  advertiserName: row.advertiserName,
  startDate: row.startDate ?? '',
  endDate: row.endDate ?? '',
  status: row.lifecycle === 'live' ? 'DELIVERING' : 'ENDED',
  lifecycle: row.lifecycle ?? 'completed',
  lineItemCount: row.lineItemCount,
  totalImpressionsDelivered: row.totalImpressionsDelivered,
  existingPageSlug: row.existingPageSlug,
  advertiserPageSlug: row.linkedHere ? hubSlug : row.linkedElsewhere,
  existingAdvertiserPageSlug: hubSlug,
});

/** When live GAM discovery failed, the linked children still make an editable roster. */
const rosterFromChildren = (detail: AdvertiserPageDetail): HubRosterRow[] =>
  detail.children
    .filter((c) => !c.revokedAt)
    .map((c) => ({
      gamOrderId: c.gamOrderId,
      gamOrderName: c.gamOrderName,
      advertiserId: c.advertiserId,
      advertiserName: c.advertiserName,
      status: '',
      startDate: c.flightStartDate,
      endDate: c.flightEndDate,
      lifecycle: c.lifecycle,
      lineItemCount: 0,
      totalImpressionsDelivered: c.pacingSummary?.impressionsDelivered ?? 0,
      source: detail.page.includeOrderIds.includes(c.gamOrderId) ? 'include' : 'advertiser',
      crossAdvertiser: !detail.page.gamAdvertiserIds.includes(c.advertiserId),
      existingPageSlug: c.slug,
      linkedHere: true,
      linkedElsewhere: null,
      included: detail.page.includeOrderIds.includes(c.gamOrderId),
      excluded: detail.page.excludeOrderIds.includes(c.gamOrderId),
      monthSlice: c.requestedEndDate < c.flightEndDate || c.reportStartDate > c.flightStartDate,
    }));

const ManageAdvertiserPageModal: React.FC<ManageAdvertiserPageModalProps> = ({
  slug,
  groups,
  refreshKey = 0,
  onClose,
  onSaved,
  onCreateCampaign,
  notify,
}) => {
  const [detail, setDetail] = useState<AdvertiserPageDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [label, setLabel] = useState('');
  const [showUpcoming, setShowUpcoming] = useState(true);
  const [autoSync, setAutoSync] = useState(true);
  const [excludeIds, setExcludeIds] = useState<Set<string>>(new Set());
  const [includeIds, setIncludeIds] = useState<string[]>([]);
  const [advertiserIds, setAdvertiserIds] = useState<string[]>([]);
  /** Includes added this session that the loaded roster doesn't know yet (display metadata). */
  const [pendingIncludes, setPendingIncludes] = useState<Map<string, PendingInclude>>(new Map());
  const [aliasQuery, setAliasQuery] = useState('');
  const [orderQuery, setOrderQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const [crossConfirm, setCrossConfirm] = useState<CrossConfirmState | null>(null);

  // Load (and re-load on refreshKey) — form state re-seeds from the fresh page.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    getAdvertiserPage(slug)
      .then((d) => {
        if (cancelled) return;
        setDetail(d);
        setLabel(d.page.clientLabel);
        setShowUpcoming(d.page.showUpcoming);
        setAutoSync(d.page.autoSync);
        setExcludeIds(new Set(d.page.excludeOrderIds ?? []));
        setIncludeIds([...(d.page.includeOrderIds ?? [])]);
        setAdvertiserIds([...(d.page.gamAdvertiserIds ?? [])]);
        setPendingIncludes(new Map());
        setCrossConfirm(null);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err?.message || 'Failed to load the advertiser page');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, refreshKey]);

  const page = detail?.page ?? null;
  const roster = useMemo<HubRosterRow[]>(() => {
    if (!detail) return [];
    return detail.roster ?? rosterFromChildren(detail);
  }, [detail]);
  const rosterIds = useMemo(() => new Set(roster.map((r) => r.gamOrderId)), [roster]);

  /**
   * This brand's orders booked under OTHER GAM advertisers (a re-booked "new
   * seat id", an agency seat) — from the picker rows the tab already holds, no
   * extra GAM call. Admin suggestions only: Include → includeOrderIds (typed
   * confirmation on save), Dismiss → excludeOrderIds. Never client-visible
   * until included. Discovery can't see these: it is scoped to the hub's ids.
   */
  const suggestions = useMemo(
    () =>
      relatedOrdersForHub({
        groups,
        labels: [page?.advertiserName ?? '', label],
        advertiserIds,
        knownOrderIds: rosterIds,
      }),
    [groups, page?.advertiserName, label, advertiserIds, rosterIds],
  );
  const suggestionIds = useMemo(() => new Set(suggestions.map((s) => s.gamOrderId)), [suggestions]);

  /** Advertiser names: picker groups, roster rows, and the page's own primary name. */
  const advertiserNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const g of groups) if (g.advertiserId) m.set(g.advertiserId, g.advertiserName);
    for (const r of roster) if (r.advertiserId && !m.has(r.advertiserId)) m.set(r.advertiserId, r.advertiserName);
    if (page?.gamAdvertiserIds[0]) m.set(page.gamAdvertiserIds[0], page.advertiserName);
    return m;
  }, [groups, roster, page]);
  const advertiserName = (id: string) => advertiserNameById.get(id) ?? `Advertiser ${id}`;

  const candidateOrders = useMemo(() => groups.flatMap((g) => g.orders), [groups]);
  const orderNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const o of candidateOrders) m.set(o.gamOrderId, o.gamOrderName);
    for (const r of roster) m.set(r.gamOrderId, r.gamOrderName);
    pendingIncludes.forEach((v, k) => m.set(k, v.gamOrderName));
    return m;
  }, [candidateOrders, roster, pendingIncludes]);

  // ---- roster include/exclude -------------------------------------------

  const rowChecked = (row: HubRosterRow): boolean =>
    row.source === 'include' ? includeIds.includes(row.gamOrderId) && !excludeIds.has(row.gamOrderId) : !excludeIds.has(row.gamOrderId);

  const toggleRow = (row: HubRosterRow) => {
    const id = row.gamOrderId;
    if (rowChecked(row)) {
      if (row.source === 'include') setIncludeIds((prev) => prev.filter((x) => x !== id));
      else setExcludeIds((prev) => new Set(prev).add(id));
    } else {
      setExcludeIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      if (row.source === 'include') setIncludeIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    }
  };

  // ---- aliases -----------------------------------------------------------

  const aliasQ = aliasQuery.trim().toLowerCase();
  const aliasResults = aliasQ
    ? groups
        .filter((g) => g.advertiserId && !advertiserIds.includes(g.advertiserId) && g.advertiserName.toLowerCase().includes(aliasQ))
        .slice(0, MAX_SEARCH_RESULTS)
    : [];

  const addAlias = (g: AdvertiserCandidateGroup) => {
    setAdvertiserIds((prev) => (prev.includes(g.advertiserId) ? prev : [...prev, g.advertiserId]));
    setAliasQuery('');
  };
  const removeAlias = (id: string) => setAdvertiserIds((prev) => prev.filter((x, i) => i === 0 || x !== id));

  // ---- specific-order includes ------------------------------------------

  const orderQ = orderQuery.trim().toLowerCase();
  const orderResults = orderQ
    ? candidateOrders
        .filter(
          (o) =>
            !rosterIds.has(o.gamOrderId) && !suggestionIds.has(o.gamOrderId) && !includeIds.includes(o.gamOrderId) && matchesQuery(o, orderQ),
        )
        .slice(0, MAX_SEARCH_RESULTS)
    : [];
  const rawIdOffer =
    /^\d+$/.test(orderQ) && !rosterIds.has(orderQ) && !includeIds.includes(orderQ) && !orderResults.some((o) => o.gamOrderId === orderQ)
      ? orderQ
      : null;

  const addInclude = (id: string, meta: PendingInclude) => {
    setIncludeIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    setExcludeIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    setPendingIncludes((prev) => new Map(prev).set(id, meta));
    setOrderQuery('');
  };
  const removePendingInclude = (id: string) => {
    setIncludeIds((prev) => prev.filter((x) => x !== id));
    setPendingIncludes((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  };
  // Suggested (related) orders show their include state in their own block.
  const newIncludes = includeIds.filter((id) => !rosterIds.has(id) && !suggestionIds.has(id));
  const dismissSuggestion = (id: string) => setExcludeIds((prev) => new Set(prev).add(id));
  const undismissSuggestion = (id: string) =>
    setExcludeIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });

  // ---- save ---------------------------------------------------------------

  const buildPatch = (): UpdateAdvertiserPageInput => {
    if (!page) return {};
    const patch: UpdateAdvertiserPageInput = {};
    if (label.trim() && label.trim() !== page.clientLabel) patch.clientLabel = label.trim();
    if (showUpcoming !== page.showUpcoming) patch.showUpcoming = showUpcoming;
    if (autoSync !== page.autoSync) patch.autoSync = autoSync;
    if (!sameIdSet(includeIds, page.includeOrderIds ?? [])) patch.includeOrderIds = includeIds;
    const excl = Array.from(excludeIds);
    if (!sameIdSet(excl, page.excludeOrderIds ?? [])) patch.excludeOrderIds = excl;
    if (!sameIdList(advertiserIds, page.gamAdvertiserIds ?? [])) patch.gamAdvertiserIds = advertiserIds;
    return patch;
  };

  const submit = async (patch: UpdateAdvertiserPageInput) => {
    setSaving(true);
    try {
      const { relink } = await updateAdvertiserPage(slug, patch);
      const parts: string[] = [];
      if (relink) {
        if (relink.linked.length > 0) parts.push(`${plural(relink.linked.length, 'page')} linked`);
        if (relink.unlinked.length > 0) parts.push(`${plural(relink.unlinked.length, 'page')} unlinked`);
        if (parts.length === 0) parts.push('New pages generate in the background — see the Sync column.');
      }
      notify('success', 'Advertiser page updated', parts.join(' · ') || undefined);
      setCrossConfirm(null);
      onSaved();
      onClose();
    } catch (err: any) {
      if (isCrossAdvertiserError(err)) {
        setCrossConfirm({ orders: err.body.orders, patch, typed: '' });
      } else {
        notify('error', 'Update failed', err?.message);
      }
    } finally {
      setSaving(false);
    }
  };

  const save = () => {
    const patch = buildPatch();
    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }
    void submit(patch);
  };

  const crossConfirmed = !!crossConfirm && crossAdvertiserConfirmed(crossConfirm.orders, crossConfirm.typed);

  const sync = page?.sync;
  const syncSpec = page ? describeSync(page) : null;

  return (
    <Modal
      open
      onClose={onClose}
      eyebrow="Advertiser pages"
      title={page ? `Manage — ${page.clientLabel}` : 'Manage advertiser page'}
      width={780}
      closeOnEsc={false}
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          {crossConfirm ? (
            <Button
              variant="primary"
              disabled={saving || !crossConfirmed}
              onClick={() => submit({ ...crossConfirm.patch, confirmCrossAdvertiser: true })}
            >
              {saving ? 'Saving…' : 'Confirm & save'}
            </Button>
          ) : (
            <Button variant="primary" onClick={save} disabled={saving || !page}>
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          )}
        </div>
      }
    >
      {loading && !detail ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center', padding: '30px 0', color: 'var(--fg-3)', fontSize: 13 }}>
          <Spinner /> Loading the page and discovering its GAM campaigns…
        </div>
      ) : loadError || !page ? (
        <div style={{ background: 'var(--danger-soft)', color: 'var(--danger)', borderRadius: 'var(--radius-md)', padding: '10px 12px', fontSize: 13 }}>
          {loadError || 'Advertiser page not found'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ fontSize: 12.5, color: 'var(--fg-4)' }}>
            <a href={page.url || advertiserPageUrl(page.slug)} target="_blank" rel="noreferrer">
              /advertisers/{page.slug}/reports
            </a>{' '}
            · {page.advertiserName} — slug and PIN have their own flows.
          </div>

          {crossConfirm && (
            <CrossAdvertiserConfirm
              orders={crossConfirm.orders}
              clientLabel={page.clientLabel}
              typed={crossConfirm.typed}
              onTyped={(typed) => setCrossConfirm({ ...crossConfirm, typed })}
              onBack={() => setCrossConfirm(null)}
              orderNameById={orderNameById}
            />
          )}

          {syncSpec && page.syncing && (
            <div style={{ background: 'var(--accent-soft)', color: 'var(--accent-soft-fg)', borderRadius: 'var(--radius-md)', padding: '9px 12px', fontSize: 12.5, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Spinner size={14} /> {syncSpec.label} Roster edits still save; new pages generate after the running sync finishes.
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>Client label</label>
              <input style={fieldStyle} value={label} onChange={(e) => setLabel(e.target.value)} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, justifyContent: 'center' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13 }}>
                <input type="checkbox" checked={autoSync} onChange={(e) => setAutoSync(e.target.checked)} />
                Auto-add new campaigns (checked hourly)
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13 }}>
                <input type="checkbox" checked={showUpcoming} onChange={(e) => setShowUpcoming(e.target.checked)} />
                Show upcoming campaigns to the client
              </label>
            </div>
          </div>

          {/* ---- Roster ---- */}
          <div>
            <label style={labelStyle}>Campaigns ({roster.filter(rowChecked).length} of {roster.length} shown to the client)</label>
            {detail?.roster === null && (
              <div style={{ background: 'var(--warning-soft)', color: 'var(--fg-1)', borderRadius: 'var(--radius-md)', padding: '8px 12px', fontSize: 12.5, marginBottom: 6 }}>
                Live GAM discovery failed — showing the linked campaign pages only. Use Sync now to retry.
              </div>
            )}
            {detail?.rosterTruncated && (
              <div style={{ background: 'var(--warning-soft)', color: 'var(--fg-1)', borderRadius: 'var(--radius-md)', padding: '8px 12px', fontSize: 12.5, marginBottom: 6 }}>
                {syncReasonLabel('discovery_truncated')}.
              </div>
            )}
            {(detail?.rosterWarnings ?? [])
              .filter((w) => w.reason !== 'discovery_truncated')
              .map((w, i) => (
                <div key={i} style={{ fontSize: 12, color: 'var(--warning)', marginBottom: 4 }}>
                  {syncReasonLabel(w.reason)}
                  {w.gamOrderId ? ` — order ${w.gamOrderId}` : ''}
                  {w.detail ? ` (${w.detail})` : ''}
                </div>
              ))}
            {roster.length === 0 ? (
              <div style={{ ...helpStyle, padding: '10px 0' }}>No campaigns found for this advertiser in GAM.</div>
            ) : (
              <div style={{ border: '1px solid var(--border-1)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
                {roster.map((row, i) => {
                  const checked = rowChecked(row);
                  return (
                    <div key={row.gamOrderId} style={{ ...rosterRowStyle, borderTop: i === 0 ? 'none' : rosterRowStyle.borderTop, opacity: checked ? 1 : 0.62 }}>
                      <input type="checkbox" checked={checked} onChange={() => toggleRow(row)} title={checked ? 'Hide from the client' : 'Show to the client'} />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.gamOrderName}</div>
                        <div style={{ fontSize: 11, color: 'var(--fg-4)' }}>
                          Order {row.gamOrderId} · {fmtDate(row.startDate)} → {row.endDate ? fmtDate(row.endDate) : 'no end date'}
                          {(row.source === 'include' || row.crossAdvertiser) && <> · {row.advertiserName}{row.source === 'include' ? ' (manual include)' : ''}</>}
                          {!checked && <> · hidden from client</>}
                        </div>
                      </div>
                      <LifecyclePill lifecycle={row.lifecycle} />
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                        {row.existingPageSlug ? (
                          <>
                            <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                              {row.linkedHere ? (
                                <StatusPill variant="ok">Linked</StatusPill>
                              ) : row.linkedElsewhere ? (
                                <StatusPill variant="draft">Linked elsewhere</StatusPill>
                              ) : (
                                <StatusPill variant="approved">Will link</StatusPill>
                              )}
                              <a href={campaignPageUrl(row.existingPageSlug)} target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>
                                Open child page ↗
                              </a>
                            </span>
                            {row.monthSlice && (
                              <span style={{ fontSize: 11, color: 'var(--warning)' }}>
                                Month report only — full-flight page unavailable while this page exists
                              </span>
                            )}
                            {row.linkedElsewhere && (
                              <span style={{ fontSize: 11, color: 'var(--fg-4)' }}>
                                Linked to another advertiser page (/advertisers/{row.linkedElsewhere}/reports) — left alone
                              </span>
                            )}
                          </>
                        ) : row.lifecycle === null ? (
                          <span style={{ fontSize: 11.5, color: 'var(--fg-4)' }}>Not shown (canceled / deleted in GAM)</span>
                        ) : row.lifecycle === 'in_work' ? (
                          <span style={{ fontSize: 11.5, color: 'var(--fg-4)' }}>{syncReasonLabel('awaiting_approval')}</span>
                        ) : !row.endDate ? (
                          <>
                            <Button size="sm" variant="subtle" onClick={() => onCreateCampaign(toReportableOrder(row, slug), { slug, label: page.clientLabel })}>
                              Create with reporting window
                            </Button>
                            <span style={{ fontSize: 11, color: 'var(--fg-4)' }}>No end date — the sync can’t create this page</span>
                          </>
                        ) : (
                          <>
                            <span style={{ fontSize: 11.5, color: 'var(--fg-4)' }}>No page yet — the next sync creates it</span>
                            <Button size="sm" variant="ghost" onClick={() => onCreateCampaign(toReportableOrder(row, slug), { slug, label: page.clientLabel })}>
                              Create page now
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div style={helpStyle}>
              Unchecked campaigns are hidden from the client (their pages are unlinked on save). Checking a hidden campaign
              re-links its page, or lets the sync create one.
            </div>
          </div>

          {/* ---- Related orders under other GAM advertisers (suggestions) ---- */}
          {suggestions.length > 0 && (
            <div>
              <label style={labelStyle}>Also found under other GAM advertisers ({suggestions.length})</label>
              <div style={{ border: '1px solid var(--border-1)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
                {suggestions.map((o, i) => {
                  const dismissed = excludeIds.has(o.gamOrderId);
                  const included = !dismissed && includeIds.includes(o.gamOrderId);
                  const linkedElsewhere = o.advertiserPageSlug && o.advertiserPageSlug !== slug ? o.advertiserPageSlug : null;
                  return (
                    <div key={o.gamOrderId} style={{ ...rosterRowStyle, borderTop: i === 0 ? 'none' : rosterRowStyle.borderTop, opacity: dismissed ? 0.62 : 1 }}>
                      <span style={{ width: 13 }} />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.gamOrderName}</div>
                        <div style={{ fontSize: 11, color: 'var(--fg-4)' }}>
                          Order {o.gamOrderId} · {fmtDate(o.startDate)} → {o.endDate ? fmtDate(o.endDate) : 'no end date'} · booked under{' '}
                          <strong style={{ color: 'var(--warning)' }}>{o.advertiserName}</strong>
                          {(o.totalImpressionsDelivered ?? 0) > 0 && <> · {(o.totalImpressionsDelivered ?? 0).toLocaleString()} impressions</>}
                          {dismissed && <> · dismissed</>}
                        </div>
                      </div>
                      <LifecyclePill lifecycle={o.lifecycle} />
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                        {linkedElsewhere ? (
                          <span style={{ fontSize: 11, color: 'var(--fg-4)' }}>
                            Its page is linked to another advertiser page (/advertisers/{linkedElsewhere}/reports)
                          </span>
                        ) : included ? (
                          <>
                            <StatusPill variant="approved">Included — confirm on save</StatusPill>
                            <Button size="sm" variant="ghost" onClick={() => removePendingInclude(o.gamOrderId)}>Undo</Button>
                          </>
                        ) : dismissed ? (
                          <Button size="sm" variant="ghost" onClick={() => undismissSuggestion(o.gamOrderId)}>Undo dismiss</Button>
                        ) : (
                          <span style={{ display: 'inline-flex', gap: 6 }}>
                            <Button
                              size="sm"
                              variant="subtle"
                              onClick={() => addInclude(o.gamOrderId, { gamOrderName: o.gamOrderName, advertiserName: o.advertiserName, advertiserId: o.advertiserId })}
                            >
                              Include
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => dismissSuggestion(o.gamOrderId)}>Dismiss</Button>
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={helpStyle}>
                Same brand name, different GAM advertiser — a campaign re-booked on a new seat id, or an agency booking. Include
                adds it to this page after a typed confirmation (the passcode then opens that report, spend included); Dismiss
                hides the suggestion. Nothing here is shown to the client until included.
              </div>
            </div>
          )}

          {/* ---- Specific-order includes ---- */}
          <div>
            <label style={labelStyle}>Also include a specific campaign (GAM order)</label>
            {newIncludes.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
                {newIncludes.map((id) => {
                  const meta = pendingIncludes.get(id);
                  return (
                    <span key={id} style={chipStyle} title={meta ? `${meta.advertiserName} · order ${id}` : `order ${id}`}>
                      {meta?.gamOrderName ?? `Order ${id}`}
                      {meta && meta.advertiserId && !advertiserIds.includes(meta.advertiserId) && (
                        <span style={{ color: 'var(--warning)' }}>· {meta.advertiserName}</span>
                      )}
                      <button type="button" style={chipRemoveStyle} onClick={() => removePendingInclude(id)} aria-label={`Remove order ${id}`}>
                        ✕
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
            <input
              style={fieldStyle}
              value={orderQuery}
              onChange={(e) => setOrderQuery(e.target.value)}
              placeholder="Search campaigns by name or advertiser, or paste a GAM order id…"
            />
            {(orderResults.length > 0 || rawIdOffer) && (
              <div style={resultListStyle}>
                {orderResults.map((o, i) => (
                  <button
                    key={o.gamOrderId}
                    type="button"
                    style={{ ...resultRowStyle, borderTop: i === 0 ? 'none' : resultRowStyle.borderTop }}
                    onClick={() => addInclude(o.gamOrderId, { gamOrderName: o.gamOrderName, advertiserName: o.advertiserName, advertiserId: o.advertiserId })}
                  >
                    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <strong>{o.gamOrderName}</strong> <span style={{ color: 'var(--fg-4)' }}>· {o.advertiserName} · order {o.gamOrderId}</span>
                    </span>
                    <LifecyclePill lifecycle={o.lifecycle} />
                  </button>
                ))}
                {rawIdOffer && (
                  <button
                    type="button"
                    style={{ ...resultRowStyle, borderTop: orderResults.length === 0 ? 'none' : resultRowStyle.borderTop }}
                    onClick={() => addInclude(rawIdOffer, { gamOrderName: `Order ${rawIdOffer}`, advertiserName: 'looked up on save', advertiserId: '' })}
                  >
                    <span>
                      Include order <strong>{rawIdOffer}</strong> <span style={{ color: 'var(--fg-4)' }}>· not in the picker — GAM looks it up on save</span>
                    </span>
                  </button>
                )}
              </div>
            )}
            <div style={helpStyle}>
              Orders of another GAM advertiser need a typed confirmation on save — the advertiser passcode then opens their
              full report, spend included.
            </div>
          </div>

          {/* ---- Aliases ---- */}
          <div>
            <label style={labelStyle}>Also include orders from GAM advertiser…</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
              {advertiserIds.map((id, i) => (
                <span key={id} style={chipStyle} title={`GAM advertiser ${id}`}>
                  {advertiserName(id)}
                  {i === 0 ? (
                    <span style={{ color: 'var(--fg-4)', fontSize: 11 }}>primary</span>
                  ) : (
                    <button type="button" style={chipRemoveStyle} onClick={() => removeAlias(id)} aria-label={`Remove ${advertiserName(id)}`}>
                      ✕
                    </button>
                  )}
                </span>
              ))}
            </div>
            <input
              style={fieldStyle}
              value={aliasQuery}
              onChange={(e) => setAliasQuery(e.target.value)}
              placeholder="Search GAM advertisers by name (e.g. a sister company or agency-booked alias)…"
            />
            {aliasQ && (
              <div style={resultListStyle}>
                {aliasResults.length === 0 ? (
                  <div style={{ padding: '7px 10px', fontSize: 12.5, color: 'var(--fg-4)' }}>No other advertiser in the picker matches.</div>
                ) : (
                  aliasResults.map((g, i) => (
                    <button key={g.advertiserId} type="button" style={{ ...resultRowStyle, borderTop: i === 0 ? 'none' : resultRowStyle.borderTop }} onClick={() => addAlias(g)}>
                      <span>
                        <strong>{g.advertiserName}</strong> <span style={{ color: 'var(--fg-4)' }}>· GAM advertiser {g.advertiserId}</span>
                      </span>
                      <span style={{ color: 'var(--fg-3)', fontSize: 12 }}>{plural(g.orders.length, 'order')}</span>
                    </button>
                  ))
                )}
              </div>
            )}
            <div style={helpStyle}>Every order of an alias advertiser is discovered, linked and created like the primary’s.</div>
          </div>

          {/* ---- Last sync ---- */}
          {sync && syncSpec && (
            <div style={{ background: 'var(--bg-surface-2)', borderRadius: 'var(--radius-md)', padding: '10px 12px', fontSize: 12.5, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 700 }}>Last sync</span>
                <StatusPill variant={syncSpec.variant}>{syncSpec.label}</StatusPill>
                <span style={{ color: 'var(--fg-4)' }}>
                  {sync.startedAt ? <>started {fmtDateTimePt(sync.startedAt)}</> : 'never run'}
                  {sync.finishedAt ? <> · finished {fmtDateTimePt(sync.finishedAt)}</> : null}
                  {sync.requestedBy ? <> · by {sync.requestedBy}</> : null}
                </span>
              </div>
              {(sync.startedAt || sync.finishedAt) && (
                <div>
                  Created {sync.created?.length ?? 0} · Linked {sync.linked?.length ?? 0} · Unlinked {sync.unlinked?.length ?? 0}
                  {sync.total > 0 && <> · Generated {Math.min(sync.current, sync.total)} of {sync.total}</>}
                </div>
              )}
              {syncIssueLines(sync).length > 0 && (
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {syncIssueLines(sync).map((line, i) => (
                    <li key={i}>{line}</li>
                  ))}
                </ul>
              )}
              {sync.status === 'error' && sync.errors?.length > 0 && (
                <div style={{ color: 'var(--danger)' }}>{sync.errors.join(' · ')}</div>
              )}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default ManageAdvertiserPageModal;
